import { useCallback, useMemo } from 'react';
import { useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query';

import { useAuthStore } from '@/stores/AuthState';
import { useTranslation } from '@/utils/i18n';
import { toast } from '@/stores/toast';
import { reactToRecommendation } from '@/api/reactions';
import {
  aggregateReactions,
  applyReactionToRows,
  nextReaction,
  sortedReactionEntries,
} from '@/utils/reactions';
import { DISCOVER_TRACK_KEY } from '@/constants/queryKeys';
import type { DiscoverReactionRow, DiscoverResponse, RecommendedTracksResponse } from '@/types';

type FeedCache = InfiniteData<RecommendedTracksResponse> | undefined;

/**
 * Reaction state + toggle for one discover card.
 *
 * C1 — the state is DERIVED from the query cache, never mirrored into local
 * `useState`. The previous version seeded `useState` from the card's props, so
 * the reaction only lived as long as the component instance: swiping away and
 * back, switching tab, or pulling to refresh remounted the card and re-seeded
 * it from a cache the mutation had never written to, and the reaction silently
 * vanished. Every write now goes through the cache, which is also what
 * `ReactionsSheet` (C2) renders from.
 */
export function useReactions(discovery: DiscoverResponse) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const userInfo = useAuthStore((s) => s.userInfo);
  const userId = userInfo.id;

  const { counts, userReaction } = useMemo(
    () => aggregateReactions(discovery.recommendations_to_users, userId),
    [discovery.recommendations_to_users, userId],
  );

  /**
   * Patch this recommendation's rows in EVERY discover cache holding it. The
   * full key is `['discover-track', circleId, includePublic]`; matching on the
   * prefix covers the circle the user is on plus any variant they already
   * visited, so the reaction is still there after switching circles.
   */
  const patchFeeds = useCallback(
    (update: (rows: DiscoverReactionRow[] | undefined) => DiscoverReactionRow[]) => {
      queryClient.setQueriesData<FeedCache>({ queryKey: DISCOVER_TRACK_KEY }, (cache) => {
        if (!cache) return cache;
        let touched = false;
        const pages = cache.pages.map((page) => {
          if (!page.data?.some((r) => r.documentId === discovery.documentId)) return page;
          touched = true;
          return {
            ...page,
            data: page.data.map((r) =>
              r.documentId === discovery.documentId
                ? { ...r, recommendations_to_users: update(r.recommendations_to_users) }
                : r,
            ),
          };
        });
        // Returning the same object keeps React from re-rendering the whole deck
        // when this card isn't in that particular cache.
        return touched ? { ...cache, pages } : cache;
      });
    },
    [queryClient, discovery.documentId],
  );

  const mutation = useMutation({
    mutationFn: (emoji: string) => reactToRecommendation(discovery.documentId, emoji),

    // Optimistic: the chip must move under the thumb, not after a round-trip.
    // NOTE: deliberately no `cancelQueries` here — the discover feed is an
    // infinite query, and cancelling would abort an in-flight `fetchNextPage`
    // mid-scroll. Nothing else refetches it (see `onSettled`), so there is no
    // racing response to guard against.
    onMutate: (emoji: string) => {
      const snapshot = queryClient.getQueriesData<FeedCache>({ queryKey: DISCOVER_TRACK_KEY });
      if (userId != null) {
        const optimistic = nextReaction(userReaction, emoji);
        patchFeeds((rows) =>
          applyReactionToRows(
            rows,
            { id: userId, username: userInfo.username, avatar_url: userInfo.avatar_url },
            optimistic,
          ),
        );
      }
      return { snapshot };
    },

    // The server is the arbiter of the toggle (someone could have reacted from
    // another device); re-apply its verdict over the optimistic guess.
    onSuccess: (res) => {
      if (userId == null) return;
      patchFeeds((rows) =>
        applyReactionToRows(
          rows,
          { id: userId, username: userInfo.username, avatar_url: userInfo.avatar_url },
          res.userReaction,
        ),
      );
    },

    onError: (error, _emoji, context) => {
      // Put every cache back exactly as it was — a failed reaction must not
      // leave a phantom chip behind.
      context?.snapshot?.forEach(([queryKey, data]) => queryClient.setQueryData(queryKey, data));

      // reaction-emoji-signal — the server now refuses any emoji outside the
      // qualified catalogue (400). That can only happen when the app's embedded
      // copy has drifted from the served one, and the whole point of accepting
      // that copy was that a divergence be LOUD: "action failed" would turn it
      // into a mystery for the user and a silent bug for us.
      const status = (error as { response?: { status?: number } })?.response?.status;
      toast.error(t(status === 400 ? 'toast.reactionNotAvailable' : 'toast.actionFailed'));
    },

    // Intentionally NO invalidation: refetching `discover-track` would re-run
    // the backend's shuffled feed query and reshuffle the deck under the user's
    // finger mid-swipe. The write-through above already holds the truth.
  });

  const entries = sortedReactionEntries(counts);

  return {
    counts,
    entries,
    userReaction,
    react: (emoji: string) => mutation.mutate(emoji),
    isReacting: mutation.isPending,
  };
}
